import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { useHatcheryScope } from '../../lib/scope.js'
import {
  Badge, Card, Empty, ErrorBox, Page, Spinner, StatusBadge, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { BATCH_STATUS, FLAG, fmtDate, inr, lakh } from '../../lib/format.js'

export default function Dashboard () {
  const scope = useHatcheryScope()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading, reload } = useAsync(
    () => (scope.missing ? Promise.resolve(null) : api.get(`/hatchery/summary${scope.query}`)),
    [scope.query],
    { live: ['batches', 'orders'] }
  )

  const publish = async (batch, status) => {
    const live = status === 'active'
    const ok = await confirm({
      title: live ? `Publish ${batch.batchCode}?` : `Close ${batch.batchCode} for booking?`,
      body: live
        ? 'The operator desk can book against it straight away and farmers see it on the public directory.'
        : 'It stops being offered for new bookings. Anything already booked against it is unaffected.',
      facts: [
        ['Stage', batch.plStage],
        ['Ready', fmtDate(batch.readyDate)],
        ['Available', lakh(batch.availableLakh ?? batch.totalCapacityLakh - batch.bookedLakh)]
      ],
      confirmLabel: live ? 'Publish batch' : 'Close batch',
      tone: live ? '' : 'danger'
    })
    if (!ok) return
    try {
      await api.post(`/hatchery/batches/${batch._id}/status`, { status, ...scope.body })
      toast.ok(status === 'active'
        ? `${batch.batchCode} is live — the operator desk can book against it now.`
        : `${batch.batchCode} moved to ${status}.`)
      reload()
    } catch (err) { toast.error(err.message) }
  }

  if (scope.missing) {
    return (
      <Page title="Hatchery portal">
        <div className="alert info">
          <div>
            <b>Pick a hatchery first</b>
            You are signed in as admin. Open a hatchery from the <Link to="/admin/hatcheries">hatcheries list</Link> and
            use “Open its portal view”.
          </div>
        </div>
      </Page>
    )
  }
  if (loading) return <Page title="My batches"><Spinner /></Page>
  if (error) return <Page title="My batches"><ErrorBox error={error} onRetry={reload} /></Page>

  const { hatchery, batches, orders, counts, totals } = data

  return (
    <Page
      title={scope.asAdmin ? `${hatchery.name} — portal view` : 'My production batches'}
      subtitle={`${hatchery.code} · ${hatchery.place}${hatchery.caaRegistrationNo ? ` · CAA ${hatchery.caaRegistrationNo}` : ''}`}
      crumb={scope.asAdmin ? <Link to={`/admin/hatcheries/${hatchery._id}`}>← Back to admin view</Link> : null}
      actions={<Link className="btn primary" to={`/hatchery/batches/new${scope.link}`}>List a new batch</Link>}
    >
      <div className="grid four" style={{ marginBottom: 18 }}>
        <div className="stat"><div className="n">{counts.live}</div><div className="k">Live batches</div><div className="sub">{counts.draft} draft awaiting publish</div></div>
        <div className="stat"><div className="n">{counts.openOrders}</div><div className="k">Open bookings</div><div className="sub">from the operator desk</div></div>
        <div className="stat"><div className="n">{lakh(totals.seedLakh)}</div><div className="k">Seed sold via platform</div></div>
        <div className="stat"><div className="n">{inr(totals.paidOut)}</div><div className="k">Received from platform</div><div className="sub">settled before packing</div></div>
      </div>

      {counts.flagged > 0 && (
        <div className="alert warn">
          <div>
            <b>A batch of yours is flagged after a failed pond survival test.</b>
            The platform will call you before any further dispatch from that batch. Nothing else is on hold.
          </div>
        </div>
      )}

      <Card title="Production batches" padded={false}>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>Batch</th><th>Ready date</th><th>Stage</th><th className="num">Available</th>
                <th className="num">Price / 1,000</th><th>Offer</th><th>Quality</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b._id}>
                  <td>
                    <Link className="rowlink mono" to={`/hatchery/batches/${b._id}${scope.link}`}>{b.batchCode}</Link>
                    <span className="t-sub">{b.species} · {b.broodstockSource}</span>
                  </td>
                  <td className="tight">{fmtDate(b.readyDate)}</td>
                  <td>{b.plStage}<span className="t-sub">{b.salinityPpt} ppt · stress {b.stressTestPct}%</span></td>
                  <td className="num">{lakh(b.availableLakh)}<span className="t-sub">of {lakh(b.totalCapacityLakh)}</span></td>
                  <td className="num">{inr(b.basePricePer1000)}</td>
                  <td>
                    {b.offer?.type && b.offer.type !== 'none'
                      ? <span className="pill-offer">{b.offer.details || b.offer.type}</span>
                      : <span className="muted small">—</span>}
                  </td>
                  <td><Badge tone={FLAG[b.qualityFlag].tone}>{FLAG[b.qualityFlag].label}</Badge></td>
                  <td><Badge tone={BATCH_STATUS[b.status].tone}>{BATCH_STATUS[b.status].label}</Badge></td>
                  <td className="right nowrap">
                    {b.status === 'draft' && <button className="btn sm primary" onClick={() => publish(b, 'active')}>Publish</button>}
                    {b.status === 'active' && <button className="btn sm" onClick={() => publish(b, 'closed')}>Close</button>}
                    {b.status === 'closed' && <button className="btn sm" onClick={() => publish(b, 'active')}>Reopen</button>}
                    {' '}
                    <Link className="btn sm" to={`/hatchery/batches/${b._id}/edit${scope.link}`}>Edit</Link>
                  </td>
                </tr>
              ))}
              {!batches.length && <tr><td colSpan={9}><Empty>No batches listed yet. Use “List a new batch” to publish your first production cycle.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Bookings on your batches" padded={false}
        actions={<Link className="btn sm" to={`/hatchery/orders${scope.link}`}>See all</Link>}>
        <div className="scroll-x">
          <table>
            <thead>
              <tr><th>Order</th><th>Farmer</th><th>Stocking date</th><th className="num">Qty</th><th>Status</th></tr>
            </thead>
            <tbody>
              {orders.slice(0, 8).map(o => (
                <tr key={o._id}>
                  <td className="mono">{o.orderCode}<span className="t-sub">{o.batch?.batchCode}</span></td>
                  <td>{o.farmer?.name}<span className="t-sub">{[o.farmer?.village, o.farmer?.mandal].filter(Boolean).join(', ')}</span></td>
                  <td className="tight">{fmtDate(o.targetStockingDate)}</td>
                  <td className="num">{lakh(o.quantityLakh)}</td>
                  <td><StatusBadge status={o.status} /></td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={5}><Empty>No open bookings right now.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </Page>
  )
}
